import { computed, inject } from "@angular/core";
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { Observable, Subscription } from "rxjs";
import { PayerConfigService } from "../services/payer/payer-config.service";
import { SettingsKeys, SettingsService } from "../services/settings/settings.service";
import { ExportService } from "../services/files/export.service";
import { PayerConfigQuery } from "../models/payer-config-query";
import { PayerConfigFilterValues } from "../models/payer-config-filter-values";
import { PayerConfigItem, PayerRiskAdjustmentPolicy } from "../models/payer-config-item";
import { PayerConfigGroup } from "../models/payer-config-group";
import { QueryMetadata } from "../models/query-metadata";
import { PayerPolicyFunctions } from "../helpers/payer-policy-functions";
import { FieldFilterService } from "../services/filters/field-filter.service";
import { FilterSelectItem } from "../models/filter-select-item";
import { PreferredActionType } from "../models/preferred-action-type";

// Page state for the Payer Configuration screen (VC-3094): the payer list grouped from config-list,
// the Preferred Action filter options and the saved page size. Provide it on the page component so it lives and dies
// with the page. The payer being edited in a dialog stays in PayerEditStore, which the payer and
// risk policy dialogs read (and clear when they close).

type PayerConfigurationState = {
    // undefined until the first load completes (the grid shows its loading overlay meanwhile)
    groups: PayerConfigGroup[] | undefined;
    loading: boolean;
    // 0 = no saved preference
    pageSize: number;
    // Last query sent; reused by export so the file matches the grid.
    query: PayerConfigQuery | null;
    // undefined until loaded; the page waits for them before its first query.
    preferredActionOptions: FilterSelectItem[] | undefined;
};

const initialState: PayerConfigurationState = {
    groups: undefined,
    // The first load starts as soon as the page's filters are ready.
    loading: true,
    pageSize: 0,
    query: null,
    preferredActionOptions: undefined,
};

/*** HELPERS ***/

// The Preferred Action filter offers only these submission types.
const PREFERRED_ACTION_FILTER_OPTIONS = ['99499', 'ASM'];

// Service year dates come from the API as display strings (M/d/yyyy); unparseable values sort first.
export const serviceYearComparator = (a?: string, b?: string) =>
    (PayerPolicyFunctions.toDate(a)?.getTime() ?? -Infinity) - (PayerPolicyFunctions.toDate(b)?.getTime() ?? -Infinity) || 0;

// Case-insensitive text sort for grid columns.
export const textComparator = (a?: string | null, b?: string | null) =>
    (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });

// config-list returns one row per payer + policy; fold them into one group per payer.
export const groupByPayer = (items: PayerConfigItem[]): PayerConfigGroup[] => {
    const groups = new Map<number, PayerConfigGroup>();

    items.forEach(item => {
        let group = groups.get(item.PayerID);

        if (!group) {
            group = {
                PayerID: item.PayerID,
                PayerName: item.PayerName,
                Abbreviation: item.Abbreviation,
                Aliases: item.Aliases,
                Status: item.Status,
                Item: item,
                Rows: []
            };
            groups.set(item.PayerID, group);
        }

        if (item.RiskAdjustmentPolicy?.PolicyID) {
            group.Rows.push(item);
        }
    });

    groups.forEach(group => group.Rows.sort((a, b) =>
        serviceYearComparator(b.RiskAdjustmentPolicy.ServiceYearStart, a.RiskAdjustmentPolicy.ServiceYearStart)));

    return [...groups.values()];
};

/*** STORE ***/

export const PayerConfigurationStore = signalStore(
    withState(initialState),
    withComputed((state) => ({
        payerCount: computed(() => state.groups()?.length ?? 0),
        policyCount: computed(() => (state.groups() ?? []).reduce((n, g) => n + g.Rows.length, 0)),
    })),
    /** METHODS AVAILABLE TO COMPONENTS **/
    withMethods((store,
        configService = inject(PayerConfigService),
        settingsService = inject(SettingsService),
        exportService = inject(ExportService),
        filterService = inject(FieldFilterService)) => {

        // Only the latest load applies; an older response arriving late is dropped.
        let loadSub: Subscription | undefined;

        // Loads the whole filtered list (PageSize 0) so every policy can be grouped under its
        // payer; paging and sorting run client-side.
        const loadPayerConfigs = (filters: Omit<PayerConfigQuery, 'Metadata'>): void => {
            const query: PayerConfigQuery = { ...filters, Metadata: new QueryMetadata() };

            patchState(store, { loading: true, query });
            loadSub?.unsubscribe();
            loadSub = configService.getPayerConfigs(query).subscribe({
                next: result => patchState(store, { groups: groupByPayer(result.Items ?? []), loading: false }),
                error: () => patchState(store, { loading: false })
            });
        };

        return {
            loadPayerConfigs,
            // Re-runs the last query, e.g. after a payer or policy is saved.
            reload(): void {
                const { query } = getState(store);
                if (query) {
                    loadPayerConfigs(query);
                }
            },
            // A payer's full policy list (with incoming file types), for the edit / copy policy dialog.
            getPayerPolicies(payerID: number): Observable<PayerRiskAdjustmentPolicy[]> {
                return configService.getPayerRiskPolicies(payerID);
            },
            savePageSize(pageSize: number): void {
                patchState(store, { pageSize });
                settingsService.updatePaginatorPageSize(pageSize, SettingsKeys.PAYER_CONFIG_PAGE_SIZE).subscribe();
            },
            exportPayerConfigs(filterValues: PayerConfigFilterValues): void {
                const { query } = getState(store);
                if (query) {
                    exportService.exportPayerConfig(query, filterValues);
                }
            },
            /** internal **/
            _loadPageSize(): void {
                settingsService.getPaginatorPageSize(SettingsKeys.PAYER_CONFIG_PAGE_SIZE).subscribe(pageSize => {
                    patchState(store, { pageSize: pageSize ?? 0 });
                });
            },
            _loadPreferredActionOptions(): void {
                filterService.getPreferredActionOptions(`${PreferredActionType.Opportunity}`).subscribe((items: FilterSelectItem[]) => {
                    const options = (items ?? [])
                        .filter(i => PREFERRED_ACTION_FILTER_OPTIONS.includes(i.Description))
                        .sort((a, b) => textComparator(a.Description, b.Description));
                    patchState(store, { preferredActionOptions: options });
                });
            },
            _cancelLoad(): void {
                loadSub?.unsubscribe();
            },
        };
    }),
    /** LIFECYCLE HOOKS **/
    withHooks({
        onInit(store) {
            store._loadPageSize();
            store._loadPreferredActionOptions();
        },
        onDestroy(store) {
            store._cancelLoad();
        },
    }),
);
